<template>
  <div v-if="isLoading || baseReportRequired" class="mt-6">
    <v-progress-linear v-if="isLoading" color="primary" indeterminate rounded />

    <v-alert
      v-else-if="!candidates.length"
      type="warning"
      class="mb-0"
      text
      dense
    >
      {{ $t('appointment.formCreate.labels.noBaseReport') }}
    </v-alert>

    <v-select
      v-else
      :value="appointmentFields.base_report_id"
      @change="select"
      :items="items"
      :rules="[rules.required]"
      :label="$t('appointment.formCreate.labels.baseReport')"
      :hint="$t('appointment.formCreate.labels.baseReportHint')"
      :menu-props="{ offsetY: true }"
      persistent-hint
      outlined
    >
      <template v-slot:selection="{ item }">
        <span><strong>#{{ item.code }}</strong> - {{ item.date }}</span>
      </template>
      <template v-slot:item="{ item }">
        <span><strong>#{{ item.code }}</strong> - {{ item.date }}</span>
      </template>
    </v-select>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { required } from '@/util/errorHandling'
import { fetchBaseReportCandidates } from '@/services/appointment/appointmentService'
import { catalogDateWithoutTime } from '@/util/catalog'

/*
 * Service types linked to a report model that requires a base report produce a comparison report,
 * built from a finished report of the same realty. The backend says whether one is required and which
 * reports are eligible; the step can only move on once one is picked.
 */
export default {
  name: 'BaseReportSelect',

  data() {
    return {
      rules: { required },
      isLoading: false,
      candidates: [],
      requestId: 0,
    }
  },

  computed: {
    ...mapGetters([
      'appointmentFields',
      'checklistLinkedAppointmentTypes',
      'baseReportRequired',
    ]),

    // Linked types take a single realty, so only that case can need a base report
    lookupKey() {
      const { type, realties, real_estate_id } = this.appointmentFields
      const isLinkedType = (this.checklistLinkedAppointmentTypes || []).includes(type)

      if (!isLinkedType || !realties || realties.length !== 1) return null
      return [type, realties[0], real_estate_id || ''].join('|')
    },

    items() {
      return this.candidates.map((candidate) => {
        const date = catalogDateWithoutTime(candidate.finish_date)
        return {
          value: candidate.asset_report_id,
          text: `#${candidate.job_code} - ${date}`,
          code: candidate.job_code,
          date,
        }
      })
    },
  },

  watch: {
    lookupKey: {
      immediate: true,
      handler() {
        this.reload()
      },
    },
  },

  methods: {
    ...mapMutations(['setAppointmentFields', 'setBaseReportRequired']),
    ...mapActions(['showErrorMessage']),

    select(baseReportId) {
      this.setAppointmentFields({ base_report_id: baseReportId })
    },

    clear() {
      this.candidates = []
      this.setBaseReportRequired(false)
      if (this.appointmentFields.base_report_id) this.select(null)
    },

    // Also called after a failed submit: the picked report may have been taken meanwhile
    async reload() {
      const requestId = ++this.requestId
      this.clear()
      if (!this.lookupKey) return

      const { type, realties, real_estate_id } = this.appointmentFields
      this.isLoading = true
      // Hold the step while loading, until the backend says whether it is needed
      this.setBaseReportRequired(true)

      try {
        const { data } = await fetchBaseReportCandidates({
          realtyId: realties[0],
          type,
          realEstateId: real_estate_id || undefined,
        })
        if (requestId !== this.requestId) return

        this.candidates = data?.candidates || []
        this.setBaseReportRequired(!!data?.required)
      } catch ({ data }) {
        if (requestId !== this.requestId) return

        // Fail closed: without the list the step cannot tell whether a base report is needed
        this.setBaseReportRequired(true)
        data?.errors?.length
          ? this.showErrorMessage(data.errors[0].message)
          : this.showErrorMessage(data?.message)
      } finally {
        if (requestId === this.requestId) this.isLoading = false
      }
    },
  },
}
</script>
